import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { AIChatInputContent, Image } from '@vyce/core/src/types';
import { compressFile } from '@vyce/core/src/components/ImageUploader/utils';

import { QualificationFields } from './types';
import { emptyQualificationFields } from './config';

dayjs.extend(customParseFormat);

// The model sometimes invents non-existent days, e.g. "2030-02-29" for a card
// showing "Feb 2030" (2030 is not a leap year) — lenient dayjs parsing accepts
// that string, so validate strictly. A day that merely overflows the month is
// clamped to the month's last day (the prompt asks for end-of-month anyway);
// anything else is dropped so the user picks the date manually.
export const normalizeExpiryDate = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return '';
  }
  if (dayjs(trimmed, 'YYYY-MM-DD', true).isValid()) {
    return trimmed;
  }
  const day = Number(trimmed.slice(8));
  const monthStart = dayjs(`${trimmed.slice(0, 7)}-01`, 'YYYY-MM-DD', true);
  if (!monthStart.isValid() || day < 29 || day > 31) {
    return '';
  }
  return monthStart.endOf('month').format('YYYY-MM-DD');
};

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.9;

// Draw the source image onto a canvas and export a genuine JPEG, so both the AI
// call and the stored document are image/jpeg (the AWS proxy and upload expect that).
const drawToJpeg = (file: File, name: string): Promise<File> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Canvas toBlob returned null'));
            return;
          }
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for JPEG conversion'));
    };
    img.src = objectUrl;
  });

// Convert any uploaded format (PNG, HEIC, …) into a compressed real JPEG. Falls
// back to the original file if the canvas step fails for any reason.
export const toJpeg = async (file: File, name: string): Promise<File> => {
  try {
    const compressed = await compressFile(file);
    return await drawToJpeg(compressed, name);
  } catch (e) {
    console.error('Qualification photo JPEG conversion failed', e);
    return file;
  }
};

// Read a JPEG file as a base64 data URL the OpenAI Responses proxy accepts inline.
export const fileToImageContent = (file: File): Promise<AIChatInputContent> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve({ type: 'input_image', image_url: reader.result as string });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const photosToImageContent = async (photos: Image[]): Promise<AIChatInputContent[]> => {
  const files = photos.map(photo => photo.file).filter((file): file is File => !!file);
  return Promise.all(files.map(fileToImageContent));
};

// Parse the model's minified-JSON answer defensively: strip code fences, take the
// JSON substring, normalize the expiry date so we never render "Invalid Date" or
// send a non-existent one. Any failure yields empty fields so the user can still
// fill everything in manually.
export const parseFields = (raw: string): QualificationFields => {
  try {
    const cleaned = raw
      .replace(/```json/gi, '')
      .replace(/```/g, '')
      .trim();
    const start = cleaned.indexOf('{');
    const end = cleaned.lastIndexOf('}');
    if (start === -1 || end === -1) {
      return { ...emptyQualificationFields };
    }
    const parsed = JSON.parse(cleaned.slice(start, end + 1));
    return {
      full_name: parsed.full_name || '',
      reference_number: parsed.reference_number || '',
      qualification_name: parsed.qualification_name || '',
      issuing_body: parsed.issuing_body || '',
      expiry_date: normalizeExpiryDate(parsed.expiry_date),
    };
  } catch (e) {
    console.error('Failed to parse qualification extraction', e);
    return { ...emptyQualificationFields };
  }
};
